import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { shiftDays, type Dataset } from "@otrack/domain";
import { Icon } from "@otrack/ui";
import { projectLabel } from "../commands";
import { formatClock, formatDuration, MINUTE } from "../time";
import { entryEdgeLimits, resolveEntryShift, type Draft } from "./actions";
import { atMinute, colorOf, DAY_MINUTES, isEditablePlan, isEntry, placeInDay, plannedIn, planTone, READ_ONLY, trackedIn, type CalItem, type EntryItem, type Placed, type PlanItem } from "./model";

export const MIN_HOUR = 32;
export const MAX_HOUR = 120;
const DRAG_THRESHOLD = 4;
const LONG_PRESS = 450;
const EDGE = 48;

export interface Slot { day: number; minute: number }

type DragState =
  | { type: "create"; day: number; anchor: number; current: number; moved: boolean; x: number; y: number }
  | { type: "move"; key: string; items: CalItem[]; grab: number; delta: number; duplicate: boolean; moved: boolean; blocked: boolean; x: number; y: number }
  | { type: "resize"; item: CalItem; edge: "start" | "end"; start: number; end: number; moved: boolean; x: number; y: number }
  | { type: "marquee"; x: number; y: number; x1: number; y1: number; additive: boolean };

export interface MenuTarget { item: CalItem | null; slot: Slot | null }

const weekdayFormat = new Intl.DateTimeFormat("en-US", { weekday: "short" });
const longDay = new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric" });

const span = (top: number, bottom: number): CSSProperties => ({ top: `calc(var(--hour) * ${top / 60})`, height: `calc(var(--hour) * ${(bottom - top) / 60})` });

const farDay = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });

/** "Wed 23:38": the day is named when a time is not on the column it is drawn in. */
function dayAndClock(ts: number, near: number): string {
  const tag = Math.abs(ts - near) > 6 * 86_400_000 ? farDay.format(ts) : weekdayFormat.format(ts);
  return `${tag} ${formatClock(ts)}`;
}

/**
 * The block's times. A block cut at midnight names the other day, so "Wed 23:38 - 00:29" on
 * Thursday says where it came from without any symbol.
 */
function rangeLabel(item: { start: number; end: number }, running = false, fromBefore = false, intoAfter = false): string {
  const start = fromBefore ? dayAndClock(item.start, item.end) : formatClock(item.start);
  const end = running ? "now" : intoAfter ? dayAndClock(item.end, item.start) : formatClock(item.end);
  return `${start} - ${end}`;
}

/** Share of the column a floating plan leaves free on its left, so the entry under it stays readable. */
const PLAN_INSET = 0.3;

/** One block on the grid: an Entry, or a plan floating over the entries. */
function Block({ item, dataset, top, bottom, col, cols, inset, hour, now, selected, linked, lifted, refused, ghost, fromBefore, intoAfter }: {
  item: CalItem; dataset: Dataset; top: number; bottom: number; col: number; cols: number; inset: number; hour: number; now: number;
  selected: boolean; linked: boolean; lifted: boolean; refused: boolean; ghost: boolean; fromBefore: boolean; intoAfter: boolean;
}) {
  const entry = isEntry(item) ? item : null;
  const plan = isEntry(item) ? null : item;
  const height = (bottom - top) * hour / 60;
  const compact = height < 34;
  // An entry too short for a line of text is drawn as a thin bar at its real length, so close entries stack.
  const sliver = entry !== null && height < 12;
  const label = item.title || (entry ? projectLabel(dataset, item.projectId) : "Untitled");
  const past = plan !== null && plan.end <= now && plan.logged.length === 0;
  const resizable = !ghost && (entry ? !entry.locked : isEditablePlan(plan!));
  const classes = [
    "ot-cb",
    entry ? "ot-cb--entry" : `ot-cb--plan ot-cb--${planTone(plan!)}`,
    entry?.running ? "is-running" : "",
    entry && !entry.billable ? "is-unbilled" : "",
    entry?.locked ? "is-locked" : "",
    past ? "is-past" : "",
    plan && plan.logged.length ? "is-logged" : "",
    selected ? "is-selected" : "",
    lifted ? "is-lifted" : "",
    refused ? "is-refused" : "",
    ghost ? "is-ghost" : "",
    compact ? "is-compact" : "",
    sliver ? "is-sliver" : "",
    fromBefore || intoAfter ? "is-cut" : "",
    fromBefore ? "from-before" : "",
    intoAfter ? "into-after" : "",
    linked ? "is-linked" : ""
  ].filter(Boolean).join(" ");
  const times = rangeLabel(item, entry?.running, fromBefore, intoAfter);
  const style: CSSProperties = {
    ...span(top, bottom),
    // A hairline under each sliver keeps back-to-back entries of one Project apart.
    ...(sliver ? { height: `calc(var(--hour) * ${(bottom - top) / 60} - 1px)` } : {}),
    left: `calc(${(inset + (1 - inset) * col / cols) * 100}% + 1px)`,
    width: `calc(${((1 - inset) / cols) * 100}% - 3px)`,
    ["--pc" as string]: colorOf(dataset, item.projectId),
    ...(plan?.google ? { ["--gc" as string]: plan.google.calendar.color } : {})
  };
  const context = entry ? projectLabel(dataset, item.projectId) : item.projectId ? projectLabel(dataset, item.projectId) : plan!.google ? plan!.google.calendar.name : "";
  const aria = [entry ? (entry.running ? "Running timer" : "Time entry") : plan!.google ? `Google Calendar event, ${plan!.google.calendar.name}` : "Planned event", label, times, context, entry?.locked ? "locked" : "", plan?.google && !plan.google.editable ? "read-only" : "", plan?.logged.length ? "logged" : ""].filter(Boolean).join(", ");

  return <div
    className={classes}
    style={style}
    data-item=""
    data-key={ghost ? undefined : item.key}
    role="button"
    tabIndex={ghost ? -1 : 0}
    aria-label={aria}
    aria-pressed={selected}
  >
    {resizable && !fromBefore && <i className="ot-cb__handle ot-cb__handle--start" data-handle="start" aria-hidden="true"/>}
    <span className="ot-cb__body">
      <span className="ot-cb__title">
        {plan && <PlanGlyph plan={plan}/>}
        {plan && plan.logged.length > 0 && <Icon name="check-circle" weight="bold" className="ot-cb__glyph ot-cb__glyph--done"/>}
        {entry?.locked && <Icon name="lock" className="ot-cb__glyph"/>}
        <span className="ot-cb__label">{label}</span>
        {compact && <span className="ot-cb__time ot-mono">{fromBefore || intoAfter ? times : formatClock(item.start)}</span>}
      </span>
      {!compact && <span className="ot-cb__meta"><span className="ot-mono">{times}</span>{context && height > 54 ? <span className="ot-cb__context">{context}</span> : null}</span>}
    </span>
    {resizable && !intoAfter && !entry?.running && <i className="ot-cb__handle ot-cb__handle--end" data-handle="end" aria-hidden="true"/>}
  </div>;
}

/**
 * The mark every plan carries, so it never reads as tracked time: a repeat or a calendar. A
 * Google event without a Project adds a dot in its calendar's colour, so calendars tell apart.
 */
export function PlanGlyph({ plan }: { plan: Pick<PlanItem, "kind" | "recurring" | "google" | "projectId"> }) {
  return <>
    {plan.google && !plan.projectId && <i className="ot-cb__calendar-dot" aria-hidden="true"/>}
    <Icon name={plan.recurring ? "repeat" : "calendar"} className="ot-cb__glyph ot-cb__glyph--plan"/>
  </>;
}

/** Lays all-day items into rows across the visible days. */
function allDayRows(items: readonly PlanItem[], days: readonly number[]): Array<{ item: PlanItem; from: number; to: number; row: number }> {
  const last = shiftDays(days.at(-1)!, 1);
  const placed: Array<{ item: PlanItem; from: number; to: number; row: number }> = [];
  const rows: number[] = [];
  for (const item of [...items].sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start))) {
    if (item.end <= days[0]! || item.start >= last) continue;
    const from = Math.max(0, days.findIndex((day) => item.start < shiftDays(day, 1)));
    const to = Math.max(from, days.findLastIndex((day) => day < item.end));
    let row = rows.findIndex((end) => end < from);
    if (row < 0) { row = rows.length; rows.push(to); } else rows[row] = to;
    placed.push({ item, from, to, row });
  }
  return placed;
}

export function TimeGrid({
  days, entries, plans, dataset, now, hour, onHour, selection, onSelect, draft, onDraft, onOpenItem, onEditItem, onBackground,
  onMenu, onDay, hoverRef, commands, compactWeekends, narrow, scrollKey, notify
}: {
  days: number[];
  entries: EntryItem[];
  plans: PlanItem[];
  dataset: Dataset;
  now: number;
  hour: number;
  onHour: (hour: number) => void;
  selection: ReadonlySet<string>;
  onSelect: (keys: Set<string>) => void;
  draft: Draft | null;
  onDraft: (draft: Draft) => void;
  onOpenItem: (key: string) => void;
  onEditItem: (item: CalItem) => void;
  /** A click on empty space while something is open: close it instead of making a new block. */
  onBackground: () => boolean;
  onMenu: (x: number, y: number, target: MenuTarget) => void;
  onDay: (day: number) => void;
  hoverRef: { current: Slot | null };
  commands: {
    shiftItems: (items: readonly CalItem[], delta: number, duplicate: boolean, dayDelta?: number) => Promise<boolean>;
    resizeItem: (item: CalItem, start: number, end: number) => Promise<void>;
  };
  compactWeekends: boolean;
  narrow: boolean;
  scrollKey: string;
  notify: (message: string) => void;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const columnsRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const pointer = useRef<{ x: number; y: number; shift: boolean; alt: boolean } | null>(null);
  const [refused, setRefused] = useState<Set<string>>(new Set());
  // The block under the pointer: both halves of an item cut at midnight light up together.
  const [hovered, setHovered] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [stripDrag, setStripDrag] = useState<{ type: "create"; from: number; to: number } | { type: "move"; item: PlanItem; origin: number; at: number } | null>(null);
  const pending = useRef<{ timer: number; x: number; y: number; fired: boolean } | null>(null);
  const zoomAnchor = useRef<{ minute: number; offset: number } | null>(null);

  const items = useMemo(() => [...entries, ...plans], [entries, plans]);
  const byKey = useMemo(() => new Map(items.map((item) => [item.key, item])), [items]);
  const timedPlans = useMemo(() => plans.filter((plan) => !plan.allDay), [plans]);
  const allDay = useMemo(() => plans.filter((plan) => plan.allDay), [plans]);

  const update = (next: DragState | null) => {
    dragRef.current = next;
    setDrag(next);
  };

  /* ------------------------------------------------------------ geometry */

  const slotAt = useCallback((x: number, y: number, fine: boolean): (Slot & { index: number; time: number }) | null => {
    const columns = columnsRef.current;
    if (!columns) return null;
    const cells = [...columns.querySelectorAll<HTMLElement>("[data-day-index]")];
    if (!cells.length) return null;
    let index = cells.findIndex((cell) => { const rect = cell.getBoundingClientRect(); return x >= rect.left && x < rect.right; });
    if (index < 0) index = x < cells[0]!.getBoundingClientRect().left ? 0 : cells.length - 1;
    const top = columns.getBoundingClientRect().top;
    const step = fine ? 1 : 5;
    const minute = Math.min(DAY_MINUTES, Math.max(0, Math.round(((y - top) / hour * 60) / step) * step));
    const day = days[index]!;
    return { day, minute, index, time: atMinute(day, minute) };
  }, [days, hour]);

  /* ------------------------------------------------------------ initial scroll and zoom */

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const first = Math.min(...items.filter((item) => !("allDay" in item && item.allDay)).map((item) => {
      const day = days.find((value) => item.start < shiftDays(value, 1) && item.end > value);
      if (day === undefined) return Infinity;
      const date = new Date(Math.max(item.start, day));
      return date.getHours() * 60 + date.getMinutes();
    }));
    const minute = Number.isFinite(first) ? Math.max(0, Math.min(first - 60, 8 * 60)) : 8 * 60;
    scroller.scrollTop = minute / 60 * hour;
    // Only a new period or view resets the scroll, not every change of data.
  }, [scrollKey]);

  useLayoutEffect(() => {
    const anchor = zoomAnchor.current;
    const scroller = scrollerRef.current;
    if (!anchor || !scroller) return;
    zoomAnchor.current = null;
    scroller.scrollTop = anchor.minute / 60 * hour - anchor.offset;
  }, [hour]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const columns = columnsRef.current!;
      const scrollerTop = scroller.getBoundingClientRect().top;
      const columnsTop = columns.getBoundingClientRect().top;
      const next = Math.round(Math.min(MAX_HOUR, Math.max(MIN_HOUR, hour * (event.deltaY < 0 ? 1.12 : 1 / 1.12))));
      if (next === hour) return;
      // Keep the time under the pointer where it is while the hours grow or shrink.
      const contentOffset = columnsTop - scrollerTop + scroller.scrollTop;
      zoomAnchor.current = { minute: (event.clientY - columnsTop) / hour * 60, offset: event.clientY - scrollerTop - contentOffset };
      onHour(next);
    };
    scroller.addEventListener("wheel", onWheel, { passive: false });
    return () => scroller.removeEventListener("wheel", onWheel);
  }, [hour, onHour]);

  /* ------------------------------------------------------------ dragging */

  const moveTo = useCallback((x: number, y: number, shift: boolean, alt: boolean) => {
    const current = dragRef.current;
    if (!current) return;
    const moved = current.type === "marquee" || Math.hypot(x - current.x, y - current.y) > DRAG_THRESHOLD;
    if (current.type === "marquee") return update({ ...current, x1: x, y1: y });
    const slot = slotAt(x, y, shift);
    if (!slot) return;
    if (current.type === "create") {
      return update({ ...current, current: current.day === slot.day ? slot.time : atMinute(current.day, slot.index > days.indexOf(current.day) ? DAY_MINUTES : 0), moved: current.moved || moved });
    }
    if (current.type === "resize") {
      const item = current.item;
      let start = item.start, end = item.end;
      if (current.edge === "start") start = Math.min(slot.time, item.end - 5 * MINUTE);
      else end = Math.max(slot.time, item.start + 5 * MINUTE);
      if (isEntry(item)) {
        const limits = entryEdgeLimits(dataset, item.entry, now);
        start = Math.max(start, limits.earliest);
        end = Math.min(end, limits.latest);
      }
      return update({ ...current, start, end, moved: current.moved || moved });
    }
    const primary = byKey.get(current.key)!;
    let delta = slot.time - current.grab - primary.start;
    let blocked = false;
    const moving = current.items.filter(isEntry);
    if (moving.length && !alt) {
      const resolved = resolveEntryShift(dataset, moving.map((item) => item.entry), delta, now);
      delta = resolved.delta;
      blocked = resolved.blocked;
    }
    update({ ...current, delta, blocked, duplicate: alt, moved: current.moved || moved });
  }, [byKey, dataset, days, now, slotAt]);

  // Near the top or bottom edge while dragging, the grid scrolls on its own.
  useEffect(() => {
    if (!drag || drag.type === "marquee") return;
    let frame = 0;
    const tick = () => {
      const scroller = scrollerRef.current;
      const last = pointer.current;
      if (scroller && last) {
        const rect = scroller.getBoundingClientRect();
        const headHeight = scroller.querySelector<HTMLElement>(".ot-cal__sticky")?.offsetHeight ?? 0;
        const top = rect.top + headHeight;
        const speed = last.y < top + EDGE ? -Math.ceil((top + EDGE - last.y) / 4) : last.y > rect.bottom - EDGE ? Math.ceil((last.y - rect.bottom + EDGE) / 4) : 0;
        if (speed) {
          scroller.scrollTop += speed;
          moveTo(last.x, last.y, last.shift, last.alt);
        }
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [drag !== null && drag.type !== "marquee", moveTo]);

  const refuse = (keys: string[]) => {
    setRefused(new Set(keys));
    window.setTimeout(() => setRefused(new Set()), 460);
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const target = event.target as HTMLElement;
    const blockElement = target.closest<HTMLElement>("[data-item]");
    const key = blockElement?.dataset["key"];
    const item = key ? byKey.get(key) : undefined;
    const laneElement = target.closest<HTMLElement>("[data-day-index]");
    pointer.current = { x: event.clientX, y: event.clientY, shift: event.shiftKey, alt: event.altKey };

    if (event.pointerType === "touch") {
      // Touch scrolls; a long press opens the menu, a tap selects or creates.
      const timer = window.setTimeout(() => {
        if (!pending.current) return;
        pending.current.fired = true;
        const slot = slotAt(event.clientX, event.clientY, false);
        navigator.vibrate?.(8);
        onMenu(event.clientX, event.clientY, { item: item ?? null, slot: item ? null : slot });
      }, LONG_PRESS);
      pending.current = { timer, x: event.clientX, y: event.clientY, fired: false };
      return;
    }

    if (item) {
      const handle = target.closest<HTMLElement>("[data-handle]")?.dataset["handle"] as "start" | "end" | undefined;
      event.currentTarget.setPointerCapture(event.pointerId);
      if (handle) return update({ type: "resize", item, edge: handle, start: item.start, end: item.end, moved: false, x: event.clientX, y: event.clientY });
      const slot = slotAt(event.clientX, event.clientY, event.shiftKey);
      const group = selection.has(item.key) && selection.size > 1 ? [...selection].map((selected) => byKey.get(selected)).filter((value): value is CalItem => Boolean(value)) : [item];
      const movable = group.filter((member) => (isEntry(member) ? !member.locked && !member.running : !member.allDay && isEditablePlan(member)) || event.altKey);
      return update({ type: "move", key: item.key, items: movable.length ? movable : [], grab: (slot?.time ?? item.start) - item.start, delta: 0, duplicate: event.altKey, moved: false, blocked: false, x: event.clientX, y: event.clientY });
    }
    if (!laneElement) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    if (event.shiftKey) return update({ type: "marquee", x: event.clientX, y: event.clientY, x1: event.clientX, y1: event.clientY, additive: event.ctrlKey || event.metaKey });
    const slot = slotAt(event.clientX, event.clientY, false);
    if (!slot) return;
    update({ type: "create", day: slot.day, anchor: slot.time, current: slot.time, moved: false, x: event.clientX, y: event.clientY });
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const slot = slotAt(event.clientX, event.clientY, false);
    hoverRef.current = slot ? { day: slot.day, minute: slot.minute } : null;
    if (pending.current && Math.hypot(event.clientX - pending.current.x, event.clientY - pending.current.y) > 8) {
      window.clearTimeout(pending.current.timer);
      pending.current = null;
    }
    if (!dragRef.current) return;
    pointer.current = { x: event.clientX, y: event.clientY, shift: event.shiftKey, alt: event.altKey };
    moveTo(event.clientX, event.clientY, event.shiftKey, event.altKey);
  };

  const finishClick = (item: CalItem | undefined, x: number, y: number, onGrid: boolean, toggle: boolean) => {
    if (item) {
      if (toggle) {
        const next = new Set(selection);
        if (next.has(item.key)) next.delete(item.key); else next.add(item.key);
        return onSelect(next);
      }
      onSelect(new Set([item.key]));
      return onOpenItem(item.key);
    }
    if (onBackground() || !onGrid) return;
    const slot = slotAt(x, y, false);
    if (!slot) return;
    const start = atMinute(slot.day, Math.floor(slot.minute / 15) * 15);
    // Past time becomes an entry; time ahead can only hold a plan.
    if (start < now - 5 * MINUTE) {
      onDraft({ kind: "entry", start, end: Math.min(now, start + 30 * MINUTE), allDay: false });
    } else onDraft({ kind: "event", start, end: start + 60 * MINUTE, allDay: false });
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (pending.current) {
      window.clearTimeout(pending.current.timer);
      const fired = pending.current.fired;
      pending.current = null;
      if (fired) return;
      const key = target.closest<HTMLElement>("[data-item]")?.dataset["key"];
      return finishClick(key ? byKey.get(key) : undefined, event.clientX, event.clientY, Boolean(target.closest("[data-day-index]")), false);
    }
    const current = dragRef.current;
    update(null);
    if (!current) return;
    if (current.type === "marquee") {
      const box = { left: Math.min(current.x, current.x1), right: Math.max(current.x, current.x1), top: Math.min(current.y, current.y1), bottom: Math.max(current.y, current.y1) };
      const picked = new Set(current.additive ? selection : []);
      for (const element of columnsRef.current?.querySelectorAll<HTMLElement>("[data-item][data-key]") ?? []) {
        const rect = element.getBoundingClientRect();
        if (rect.left < box.right && rect.right > box.left && rect.top < box.bottom && rect.bottom > box.top) picked.add(element.dataset["key"]!);
      }
      return onSelect(picked);
    }
    if (current.type === "create") {
      if (!current.moved) return finishClick(undefined, event.clientX, event.clientY, true, false);
      const start = Math.min(current.anchor, current.current), end = Math.max(current.anchor, current.current);
      if (end - start < 5 * MINUTE) return;
      const entry = start < now - MINUTE;
      return onDraft({ kind: entry ? "entry" : "event", start, end: entry ? Math.min(end, now) : end, allDay: false });
    }
    if (current.type === "resize") {
      if (!current.moved) return finishClick(current.item, event.clientX, event.clientY, false, event.ctrlKey || event.metaKey);
      if (current.start !== current.item.start || current.end !== current.item.end) void commands.resizeItem(current.item, current.start, current.end);
      return;
    }
    const item = byKey.get(current.key);
    if (!current.moved) return finishClick(item, event.clientX, event.clientY, false, event.ctrlKey || event.metaKey || event.shiftKey);
    if (!current.items.length) {
      notify(item && isEntry(item) ? (item.locked ? "This entry is on a sent invoice." : "Drag the top edge to change when the timer started.") : READ_ONLY);
      return refuse([current.key]);
    }
    if (current.blocked) {
      notify("That overlaps other time");
      return refuse(current.items.map((member) => member.key));
    }
    if (current.delta === 0 && !current.duplicate) return;
    void commands.shiftItems(current.items, current.delta, current.duplicate).then((done) => { if (!done) refuse(current.items.map((member) => member.key)); });
  };

  const onContextMenu = (event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (pending.current) return;
    const key = (event.target as HTMLElement).closest<HTMLElement>("[data-item]")?.dataset["key"];
    const item = key ? byKey.get(key) ?? null : null;
    const slot = slotAt(event.clientX, event.clientY, false);
    if (item && !selection.has(item.key)) onSelect(new Set([item.key]));
    onMenu(event.clientX, event.clientY, { item, slot: item ? null : slot });
  };

  const onDoubleClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    const key = (event.target as HTMLElement).closest<HTMLElement>("[data-item]")?.dataset["key"];
    const item = key ? byKey.get(key) : undefined;
    if (item) onEditItem(item);
  };

  /* ------------------------------------------------------------ what to draw */

  const display = useMemo(() => {
    let showEntries: EntryItem[] = entries;
    let showPlans: PlanItem[] = timedPlans;
    const ghosts: CalItem[] = [];
    const lifted = new Set<string>();
    if (drag?.type === "move" && drag.moved && drag.items.length) {
      const moved = new Map(drag.items.map((item) => [item.key, item]));
      const shifted = <T extends CalItem>(item: T): T => ({ ...item, start: item.start + drag.delta, end: item.end + drag.delta });
      if (drag.duplicate) {
        for (const item of drag.items) ghosts.push({ ...shifted(item), key: `copy:${item.key}` });
      } else {
        showEntries = showEntries.map((item) => (moved.has(item.key) ? shifted(item) : item));
        showPlans = showPlans.map((item) => (moved.has(item.key) ? shifted(item) : item));
        for (const key of moved.keys()) lifted.add(key);
      }
    }
    if (drag?.type === "resize" && drag.moved) {
      const resized = { ...drag.item, start: drag.start, end: drag.end };
      if (isEntry(resized)) showEntries = showEntries.map((item) => (item.key === resized.key ? resized : item));
      else showPlans = showPlans.map((item) => (item.key === resized.key ? resized : item));
      lifted.add(drag.item.key);
    }
    return { showEntries, showPlans, ghosts, lifted };
  }, [drag, entries, timedPlans]);

  const creating = drag?.type === "create" && drag.moved ? { start: Math.min(drag.anchor, drag.current), end: Math.max(drag.anchor, drag.current) } : null;
  const nowMinute = new Date(now).getHours() * 60 + new Date(now).getMinutes();
  const hours = Array.from({ length: 23 }, (_, index) => index + 1);
  const columns = days.map((day) => (compactWeekends && [0, 6].includes(new Date(day).getDay()) ? "minmax(0, .62fr)" : "minmax(0, 1fr)")).join(" ");
  const rows = allDayRows(allDay, days);
  const rowCount = rows.reduce((max, row) => Math.max(max, row.row + 1), 0);
  const visibleRows = expanded ? rowCount : Math.min(rowCount, 2);
  const hiddenPerDay = days.map((_, index) => rows.filter((row) => row.row >= visibleRows && row.from <= index && row.to >= index).length);

  const draftBlock = (day: number): ReactNode => {
    if (!draft || draft.allDay) return null;
    const next = shiftDays(day, 1);
    if (draft.start >= next || draft.end <= day) return null;
    const top = Math.max(0, (Math.max(draft.start, day) - day) / MINUTE);
    const bottom = Math.min(DAY_MINUTES, Math.max(top + 15, (Math.min(draft.end, next) - day) / MINUTE));
    return <div className={`ot-cb ot-cb--draft ot-cb--${draft.kind === "entry" ? "entry" : "plan"}`} data-key="draft" style={span(top, bottom)}>
      <span className="ot-cb__body"><span className="ot-cb__title"><span className="ot-cb__label">{draft.kind === "entry" ? "New entry" : "New event"}</span></span><span className="ot-cb__meta ot-mono">{rangeLabel(draft)}</span></span>
    </div>;
  };

  /* ------------------------------------------------------------ all-day strip */

  const stripDay = (x: number): number => {
    const cells = [...(stripRef.current?.querySelectorAll<HTMLElement>("[data-strip-day]") ?? [])];
    const index = cells.findIndex((cell) => { const rect = cell.getBoundingClientRect(); return x >= rect.left && x < rect.right; });
    return index < 0 ? (cells.length && x >= cells.at(-1)!.getBoundingClientRect().right ? cells.length - 1 : 0) : index;
  };

  const onStripDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || event.pointerType === "touch" || (event.target as HTMLElement).closest(".ot-cal__more, .ot-cal__expand")) return;
    const key = (event.target as HTMLElement).closest<HTMLElement>("[data-key]")?.dataset["key"];
    const item = key ? allDay.find((plan) => plan.key === key) : undefined;
    const index = stripDay(event.clientX);
    event.currentTarget.setPointerCapture(event.pointerId);
    pointer.current = { x: event.clientX, y: event.clientY, shift: false, alt: event.altKey };
    setStripDrag(item ? { type: "move", item, origin: index, at: index } : { type: "create", from: index, to: index });
  };
  const onStripMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!stripDrag) return;
    const index = stripDay(event.clientX);
    setStripDrag(stripDrag.type === "move" ? { ...stripDrag, at: index } : { ...stripDrag, to: index });
  };
  const onStripUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = stripDrag;
    setStripDrag(null);
    if (!current) return;
    if (current.type === "create") {
      const from = Math.min(current.from, current.to), to = Math.max(current.from, current.to);
      if (from === to && onBackground()) return;
      return onDraft({ kind: "event", start: days[from]!, end: shiftDays(days[to]!, 1), allDay: true });
    }
    const moved = Math.hypot(event.clientX - pointer.current!.x, event.clientY - pointer.current!.y) > DRAG_THRESHOLD;
    if (!moved || current.at === current.origin) {
      if (!moved) { onSelect(new Set([current.item.key])); onOpenItem(current.item.key); }
      return;
    }
    if (!isEditablePlan(current.item) && !event.altKey) return notify(READ_ONLY);
    const dayDelta = current.at - current.origin;
    void commands.shiftItems([current.item], shiftDays(current.item.start, dayDelta) - current.item.start, event.altKey, dayDelta);
  };

  return <div className={`ot-cal__grid ${narrow ? "is-narrow" : ""}`} style={{ ["--hour" as string]: `${hour}px`, ["--cols" as string]: columns }}>
    <div className="ot-cal__scroller" ref={scrollerRef}>
      <div className="ot-cal__sticky">
        <div className="ot-cal__heads">
          <div className="ot-cal__corner"/>
          {days.map((day) => {
            const next = shiftDays(day, 1);
            const tracked = trackedIn(entries, day, next);
            const planned = plannedIn(plans, day, next);
            const today = now >= day && now < next;
            return <button key={day} type="button" className={`ot-cal__head ${today ? "is-today" : ""}`} onClick={() => onDay(day)} aria-label={`${longDay.format(day)}, ${formatDuration(tracked)} tracked${planned ? `, ${formatDuration(planned)} planned` : ""}. Open the day`}>
              <span className="ot-cal__weekday">{weekdayFormat.format(day)}</span>
              <span className="ot-cal__date">{new Date(day).getDate()}</span>
              <span className="ot-cal__totals">
                <span className={`ot-mono ${tracked ? "" : "is-zero"}`}>{tracked ? formatDuration(tracked) : "0m"}</span>
                {planned > 0 && <span className="ot-cal__planned ot-mono">/ {formatDuration(planned)}</span>}
              </span>
            </button>;
          })}
        </div>
        <div className="ot-cal__allday" ref={stripRef} onPointerDown={onStripDown} onPointerMove={onStripMove} onPointerUp={onStripUp} onPointerCancel={() => setStripDrag(null)} onContextMenu={(event) => {
          event.preventDefault();
          const key = (event.target as HTMLElement).closest<HTMLElement>("[data-key]")?.dataset["key"];
          const item = key ? allDay.find((plan) => plan.key === key) ?? null : null;
          onMenu(event.clientX, event.clientY, { item, slot: item ? null : { day: days[stripDay(event.clientX)]!, minute: -1 } });
        }}>
          <div className="ot-cal__allday-label">{rowCount > 2 ? <button type="button" className="ot-cal__expand" onClick={() => setExpanded(!expanded)} aria-expanded={expanded}><Icon name={expanded ? "caret-up" : "caret-down"}/>All day</button> : "All day"}</div>
          <div className="ot-cal__allday-cells" style={{ gridTemplateRows: `repeat(${Math.max(1, visibleRows + (hiddenPerDay.some(Boolean) ? 1 : 0))}, 24px)` }}>
            {days.map((day, index) => <div key={day} className="ot-cal__allday-cell" data-strip-day={index} style={{ gridColumn: index + 1, gridRow: `1 / -1` }}/>)}
            {stripDrag?.type === "create" && <div className="ot-cal__allday-ghost" style={{ gridColumn: `${Math.min(stripDrag.from, stripDrag.to) + 1} / ${Math.max(stripDrag.from, stripDrag.to) + 2}`, gridRow: 1 }}>New event</div>}
            {draft?.allDay && (() => {
              const from = days.findIndex((day) => day >= draft.start), to = days.findLastIndex((day) => day < draft.end);
              return from >= 0 ? <div className="ot-cal__chip ot-cal__chip--draft" data-key="draft" style={{ gridColumn: `${from + 1} / ${to + 2}`, gridRow: 1 }}>New event</div> : null;
            })()}
            {rows.filter((row) => row.row < visibleRows).map(({ item, from, to, row }) => {
              const shift = stripDrag?.type === "move" && stripDrag.item.key === item.key ? stripDrag.at - stripDrag.origin : 0;
              const start = Math.max(0, Math.min(days.length - 1, from + shift)), end = Math.max(0, Math.min(days.length - 1, to + shift));
              return <div
                key={item.key}
                data-key={item.key}
                role="button"
                tabIndex={0}
                aria-pressed={selection.has(item.key)}
                aria-label={`${item.title || "Untitled"}, all day${item.google ? `, from ${item.google.calendar.name}` : ""}`}
                className={`ot-cal__chip ot-cal__chip--${planTone(item)} ${selection.has(item.key) ? "is-selected" : ""} ${shift ? "is-lifted" : ""} ${item.start < days[0]! ? "from-before" : ""} ${item.end > shiftDays(days.at(-1)!, 1) ? "into-after" : ""}`}
                style={{ gridColumn: `${start + 1} / ${end + 2}`, gridRow: row + 1, ["--pc" as string]: colorOf(dataset, item.projectId), ...(item.google ? { ["--gc" as string]: item.google.calendar.color } : {}) }}
                onDoubleClick={() => onEditItem(item)}
              >
                <PlanGlyph plan={item}/>
                <span>{item.title || "Untitled"}</span>
              </div>;
            })}
            {hiddenPerDay.map((count, index) => count > 0 && <button key={index} type="button" className="ot-cal__more" style={{ gridColumn: index + 1, gridRow: visibleRows + 1 }} onClick={() => setExpanded(true)}>+{count} more</button>)}
          </div>
        </div>
      </div>
      <div className="ot-cal__body">
        <div className="ot-cal__gutter" aria-hidden="true">
          {hours.map((value) => <span key={value} style={{ top: `calc(var(--hour) * ${value})` }}>{String(value).padStart(2, "0")}:00</span>)}
          {days.some((day) => now >= day && now < shiftDays(day, 1)) && <span className="ot-cal__now-label ot-mono" style={{ top: `calc(var(--hour) * ${nowMinute / 60})` }}>{formatClock(now)}</span>}
        </div>
        <div
          className="ot-cal__columns"
          ref={columnsRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={() => { update(null); if (pending.current) { window.clearTimeout(pending.current.timer); pending.current = null; } }}
          onPointerLeave={() => { hoverRef.current = null; setHovered(null); }}
          onPointerOver={(event) => {
            const key = (event.target as HTMLElement).closest<HTMLElement>("[data-item]")?.dataset["key"] ?? null;
            if (key !== hovered) setHovered(key);
          }}
          onContextMenu={onContextMenu}
          onDoubleClick={onDoubleClick}
        >
          {days.map((day, index) => {
            const next = shiftDays(day, 1);
            const today = now >= day && now < next;
            const placedEntries = placeInDay([...display.showEntries, ...display.ghosts.filter(isEntry)], day, next, true);
            const placedPlans = placeInDay([...display.showPlans, ...display.ghosts.filter((ghost): ghost is PlanItem => !isEntry(ghost))], day, next);
            const aheadTop = now >= next ? null : now < day ? 0 : nowMinute;
            // Plans float over the entries; where they cover one, they step right so it stays readable.
            const covered = new Set(placedPlans.filter((plan) => placedEntries.some((entry) => entry.top < plan.bottom && entry.bottom > plan.top)).map((plan) => plan.group));
            const render = (placed: Placed<CalItem>, inset = 0) => {
              const ghost = placed.item.key.startsWith("copy:");
              return <Block
                key={placed.item.key}
                item={placed.item}
                dataset={dataset}
                top={placed.top}
                bottom={placed.bottom}
                col={placed.col}
                cols={placed.cols}
                inset={inset}
                hour={hour}
                now={now}
                selected={selection.has(placed.item.key)}
                linked={(placed.fromBefore || placed.intoAfter) && hovered === placed.item.key && !drag}
                lifted={display.lifted.has(placed.item.key)}
                refused={refused.has(placed.item.key)}
                ghost={ghost}
                fromBefore={placed.fromBefore}
                intoAfter={placed.intoAfter}
              />;
            };
            return <div key={day} className={`ot-cal__day ${today ? "is-today" : ""} ${[0, 6].includes(new Date(day).getDay()) ? "is-weekend" : ""}`} data-day-index={index} role="group" aria-label={longDay.format(day)}>
              {aheadTop !== null && <div className="ot-cal__ahead" style={{ top: `calc(var(--hour) * ${aheadTop / 60})` }} aria-hidden="true"/>}
              {placedEntries.map((placed) => render(placed))}
              {placedPlans.map((placed) => render(placed, covered.has(placed.group) ? PLAN_INSET : 0))}
              {draftBlock(day)}
              {creating && drag?.type === "create" && drag.day === day && (() => {
                // Past time becomes an entry, clipped at now; a drag that starts ahead becomes a plan.
                const plan = creating.start >= now - MINUTE;
                const end = plan ? creating.end : Math.max(creating.start, Math.min(creating.end, now));
                return <div className={`ot-cal__ghost ${plan ? "is-plan" : ""}`} style={span((creating.start - day) / MINUTE, (end - day) / MINUTE)}><span className="ot-mono">{formatDuration(end - creating.start)}</span></div>;
              })()}
              {today && <div className="ot-cal__now" style={{ top: `calc(var(--hour) * ${nowMinute / 60})` }} aria-hidden="true"/>}
            </div>;
          })}
          {drag?.type === "marquee" && <MarqueeBox drag={drag} container={columnsRef.current}/>}
        </div>
      </div>
    </div>
  </div>;
}

function MarqueeBox({ drag, container }: { drag: Extract<DragState, { type: "marquee" }>; container: HTMLElement | null }) {
  if (!container) return null;
  const rect = container.getBoundingClientRect();
  const left = Math.min(drag.x, drag.x1) - rect.left, top = Math.min(drag.y, drag.y1) - rect.top;
  return <div className="ot-cal__marquee" style={{ left, top, width: Math.abs(drag.x1 - drag.x), height: Math.abs(drag.y1 - drag.y) }} aria-hidden="true"/>;
}
